import { Box, Button } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { ReactNode, useState } from "react";

/**
 * Shared action footer for the editable submission steps (Details, Facility
 * Profile, State Info), mirroring the Tier II report's save behavior: a
 * "Cancel" button beside a single "Save".
 *
 * - **Cancel** — discard unsaved edits and return to the overview.
 * - **Save** — persist, mark the step Completed, and advance to the next step
 *   (matching Tier II). The left nav still allows jumping between sections.
 *
 * Save is disabled when there are critical compliance issues (the `disabled`
 * prop, set by the caller) — matching Tier II, which blocks saving data that
 * the engine considers critically invalid. Non-critical issues do not block the
 * save; the overview still flags the step until they're resolved.
 *
 * The caller owns persistence via `onSave`, which must resolve `true` on success
 * and `false` to abort (e.g. validation failed); the caller shows its own error
 * toast. This footer owns the success toast and the step-status touch.
 *
 * Renders nothing when the user cannot edit or the submission is frozen,
 * matching `StepContinueFooter`.
 */
export function StepSaveFooter({
  submissionId,
  step,
  onSave,
  saving = false,
  disabled = false,
  leftActions,
}: {
  submissionId: string;
  step: SubmissionStepType;
  onSave: () => Promise<boolean>;
  saving?: boolean;
  disabled?: boolean;
  leftActions?: ReactNode;
}) {
  const alerts = useAlerts();
  const { canEdit, isFrozen } = useWasteStepper();
  const { touchStep, loading: touching } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep, goToOverview } = useStepperNavigation();
  const [pending, setPending] = useState(false);

  if (isFrozen || !canEdit) {
    return null;
  }

  const busy = saving || touching || pending;

  // Persist, mark the step complete, and advance to the next step (Tier II).
  const handleSave = async () => {
    setPending(true);
    try {
      const ok = await onSave();
      if (!ok) {
        return;
      }
      await touchStep(step, SubmissionStepStatus.Completed);
      alerts.success("Saved.");
      goToNextStep(step);
    } finally {
      setPending(false);
    }
  };

  const handleCancel = () => goToOverview();

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: 3 }}>
      {leftActions && (
        <Box sx={{ mr: "auto", display: "flex", alignItems: "center", gap: 1 }}>{leftActions}</Box>
      )}
      <Button variant="text" disabled={busy} onClick={handleCancel}>
        Cancel
      </Button>
      <Button
        variant="contained"
        disabled={disabled || busy}
        loading={pending}
        onClick={handleSave}
      >
        Save
      </Button>
    </Box>
  );
}
